import React, { useMemo, useState } from "react";
import { countWords } from "@/lib/wordLimits";

/**
 * WordInput
 * Renders a labeled textarea with a live word counter, tone reminder,
 * warning + error state, save draft & submit buttons.
 *
 * Props:
 *  - stageName, prompt, toneReminder, limit (number), accent ("a" | "b")
 *  - value, onChange
 *  - onSubmit(text), onSaveDraft(text)
 *  - disabled (bool), submitLabel (string)
 *  - testIdPrefix (string)
 */
export default function WordInput({
  stageName,
  prompt,
  toneReminder,
  limit,
  accent = "a",
  value,
  onChange,
  onSubmit,
  onSaveDraft,
  disabled = false,
  submitLabel = "Submit Turn",
  testIdPrefix = "word-input",
  helperText,
  loading = false,
}) {
  const [touched, setTouched] = useState(false);

  const wordCount = useMemo(() => countWords(value || ""), [value]);
  const ratio = limit ? wordCount / limit : 0;
  const isWarning = ratio >= 0.8 && ratio <= 1;
  const isError = ratio > 1;
  const isEmpty = wordCount === 0;

  const counterColor = isError
    ? "text-[color:var(--counter-error)] counter-shake"
    : isWarning
      ? "text-[color:var(--counter-warning)] counter-pulse"
      : "text-[color:var(--text-muted)]";

  const accentRing =
    accent === "a"
      ? "focus-within:ring-[color:var(--participant-a)]/40"
      : "focus-within:ring-[color:var(--participant-b)]/40";

  const accentDot =
    accent === "a"
      ? "bg-[color:var(--participant-a)]"
      : "bg-[color:var(--participant-b)]";

  const canSubmit = !isError && !isEmpty && !disabled && !loading;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!canSubmit) {
      setTouched(true);
      return;
    }
    onSubmit?.(value);
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-wrap`}>
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full ${accentDot}`} />
        <h3 className="font-serif text-xl text-[color:var(--text-primary)]">
          {stageName}
        </h3>
      </div>
      {prompt && (
        <p className="text-sm text-[color:var(--text-muted)] leading-relaxed">
          <span className="font-medium text-[color:var(--text-primary)]">
            Prompt:
          </span>{" "}
          {prompt}
        </p>
      )}
      {toneReminder && (
        <div
          className="text-xs flex items-start gap-2 rounded-md border border-[color:var(--line)] bg-white/70 px-3 py-2 text-[color:var(--text-muted)]"
          data-testid={`${testIdPrefix}-tone-reminder`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="w-4 h-4 mt-[2px] flex-none"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{toneReminder}</span>
        </div>
      )}

      <div
        className={`relative bg-white rounded-xl border border-[color:var(--line)] focus-within:ring-2 ${accentRing} transition`}
      >
        <textarea
          data-testid={`${testIdPrefix}-textarea`}
          className="w-full resize-y min-h-[180px] rounded-xl px-5 py-4 bg-transparent outline-none text-[15px] leading-relaxed font-sans text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)]"
          placeholder="Type here..."
          value={value || ""}
          onChange={(e) => {
            setTouched(true);
            onChange?.(e.target.value);
          }}
          disabled={disabled || loading}
        />
        <div
          className={`absolute bottom-3 right-4 text-sm font-medium tabular-nums ${counterColor}`}
          data-testid={`${testIdPrefix}-counter`}
        >
          <span data-testid={`${testIdPrefix}-counter-value`}>
            {wordCount}
          </span>{" "}
          / {limit} words
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-[color:var(--text-muted)]">
          {isError && (
            <span
              className="text-[color:var(--counter-error)] font-medium"
              data-testid={`${testIdPrefix}-error`}
            >
              You have exceeded the {limit}-word limit. Please shorten your response.
            </span>
          )}
          {isWarning && !isError && (
            <span
              className="text-[color:var(--counter-warning)]"
              data-testid={`${testIdPrefix}-warning`}
            >
              You are close to the {limit}-word limit.
            </span>
          )}
          {!isWarning && !isError && helperText && <span>{helperText}</span>}
          {!isWarning && !isError && !helperText && touched && isEmpty && (
            <span data-testid={`${testIdPrefix}-empty`}>
              Write at least one word to submit your turn.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onSaveDraft && (
            <button
              type="button"
              data-testid={`${testIdPrefix}-save-draft`}
              onClick={() => onSaveDraft(value || "")}
              disabled={disabled || loading}
              className="px-4 py-2 rounded-full border border-[color:var(--line)] bg-white text-sm font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--surface-sage)] transition disabled:opacity-50"
            >
              Save Draft
            </button>
          )}
          <button
            type="button"
            data-testid={`${testIdPrefix}-submit`}
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-5 py-2 rounded-full text-sm font-semibold text-white shadow-sm transition disabled:opacity-50"
            style={{
              backgroundColor:
                accent === "a"
                  ? "var(--participant-a)"
                  : "var(--participant-b)",
            }}
          >
            {loading ? "Saving..." : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
